import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

interface Column {
  step: string;
  label: string;
  role: ReactNode;
  bullets: string[];
}

const COLUMNS: Column[] = [
  {
    step: "01",
    label: "Insights &\nRecommendations",
    role: (
      <>
        Data from 20+ sources, processed through proprietary{" "}
        <span className="font-semibold text-slate-900">ASO Engines</span> that
        embody human-curated ASO methodology.
      </>
    ),
    bullets: [
      "Live store listings",
      "User reviews & rankings",
      "Competitor movements",
      "AI/LLM discoverability (GEO)",
      "Keyword performance",
    ],
  },
  {
    step: "02",
    label: "Actions",
    role: (
      <>
        Ranked recommendations become a{" "}
        <span className="font-semibold text-slate-900">draft revision</span> —
        reviewed by you.
      </>
    ),
    bullets: [
      "Draft built from the ranked recommendations",
      "Metadata, keywords & creative in one revision",
      "One-click publish from inside Apptonomy",
    ],
  },
  {
    step: "03",
    label: "Impact",
    role: (
      <>
        Source-of-truth results from{" "}
        <span className="font-semibold text-slate-900">
          the stores themselves
        </span>
        .
      </>
    ),
    bullets: [
      "KPIs direct from App Store Connect & Play Console",
      "Every delta tied to the revision that shipped it",
      "Conversions up, paid efficiency up",
    ],
  },
];

export default function TheSolution() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-8">
      <SlideHeader eyebrow="The Solution">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Your apps require continuous on-trend ASO.
        </h1>
        <p className="mt-3 text-lg font-medium tracking-tight text-slate-700 md:mt-4 md:text-2xl">
          Not another ASO dashboard. Not a thin AI wrapper calling itself SaaS.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        {COLUMNS.map((col) => (
          <SolutionColumn key={col.label} {...col} />
        ))}
      </div>

      {/* GEO objection-answer band (Enric note 1, A7Y-1934; scale facts TPF 2026-07-30) */}
      <div className="mt-5 rounded-xl bg-slate-50 px-5 py-4 ring-1 ring-slate-200 md:mt-6 md:px-6">
        <p className="text-sm leading-snug text-slate-700 md:text-base">
          <span className="font-semibold text-slate-900">
            Why not just use AI directly?
          </span>{" "}
          The Apptonomy audit runs{" "}
          <span className="font-semibold text-slate-900">
            ~100 hand-crafted prompts across several AI models
          </span>{" "}
          — every day, for every app, in every language, on both App Store and
          Google Play. Not something anyone does by hand on a regular basis.
          And raw AI can't run{" "}
          <span className="font-semibold text-slate-900">
            GEO — generative engine optimization, SEO's successor
          </span>{" "}
          — ingest your store data, or publish your revisions. AI is an input
          to the loop; it isn't the loop.
        </p>
      </div>

      <div className="mt-3 text-center md:mt-auto">
        <p className="text-balance text-lg font-bold italic leading-snug text-violet-700 md:text-3xl">
          You require an ASO operating system.
        </p>
      </div>
    </div>
  );
}

function SolutionColumn({ step, label, role, bullets }: Column) {
  return (
    <div className="flex flex-col rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200 md:p-6">
      <p className="text-xs font-bold tracking-[0.18em] text-slate-300 md:text-sm">
        {step}
      </p>
      <h3 className="mt-1 whitespace-pre-line text-xl font-bold leading-tight tracking-tight text-blue-600 md:text-2xl">
        {label}
      </h3>
      <p className="mt-2 text-sm leading-snug text-slate-600 md:text-base">
        {role}
      </p>
      <ul className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 text-sm leading-snug text-slate-600 md:text-[15px]">
        {bullets.map((b) => (
          <li key={b} className="flex gap-2">
            <span aria-hidden="true" className="shrink-0 text-blue-600">
              →
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
